import { useState, useEffect, useCallback, useRef } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { Video, Loader2, AlertCircle } from 'lucide-react'
import { MeetingLobby } from './MeetingLobby'
import { MeetingRoom } from './MeetingRoom'
import { useMeetingStore, useActiveMeetings } from '@/hooks'
import {
  generateMeetingToken,
  generateParticipantIdentity,
  isLiveKitConfigured,
  getLiveKitUrl,
  LiveKitProvider,
  useLiveKit,
} from '@/lib/livekit'
import { ThemeProvider } from '@/lib/theme'

type View = 'lobby' | 'meeting'

// Component to render audio for remote participants
function RemoteAudio({ audioTrack }: { audioTrack: any }) {
  const audioRef = useRef<HTMLAudioElement>(null)

  useEffect(() => {
    if (audioTrack && audioRef.current) {
      audioTrack.attach(audioRef.current)
      return () => {
        audioTrack.detach(audioRef.current!)
      }
    }
  }, [audioTrack])

  return <audio ref={audioRef} autoPlay />
}

function GuestMeetingContent() {
  const { roomId: urlRoomId } = useParams<{ roomId: string }>()
  const navigate = useNavigate()
  const [view, setView] = useState<View>('lobby')
  const [isJoining, setIsJoining] = useState(false)
  const [configError, setConfigError] = useState<string | null>(null)
  const [displayName, setDisplayName] = useState('')
  const [showParticipants, setShowParticipants] = useState(false)

  // Track participant identity for database tracking
  const [participantIdentity, setParticipantIdentity] = useState<string | null>(null)

  // LiveKit context
  const {
    connect,
    disconnect,
    isConnected,
    participants,
    localParticipant,
    isMuted,
    isCameraOff,
    isScreenSharing,
    isBackgroundBlurEnabled,
    isBackgroundBlurLoading,
    blurStrength,
    toggleMute,
    toggleCamera,
    toggleScreenShare,
    toggleBackgroundBlur,
    setBlurStrength,
    switchAudioInput,
    switchVideoInput,
    switchAudioOutput,
    applyAudioSettings,
    error: roomError,
  } = useLiveKit()

  // Active meetings tracking (for joining existing meetings)
  const { joinMeeting, leaveMeeting } = useActiveMeetings()

  // Check LiveKit configuration on mount
  useEffect(() => {
    if (!isLiveKitConfigured()) {
      setConfigError('Meeting rooms are not configured. Please contact the administrator.')
    }
  }, [])

  // Update view when connected
  useEffect(() => {
    if (isConnected && view === 'lobby') {
      setView('meeting')
    }
  }, [isConnected, view])

  // Clean up meeting when user closes tab or navigates away
  useEffect(() => {
    const handleBeforeUnload = () => {
      if (urlRoomId && participantIdentity && isConnected) {
        // Use sendBeacon for reliable cleanup on tab close
        const supabaseUrl = import.meta.env.VITE_NEXUS_SUPABASE_URL

        if (supabaseUrl) {
          const payload = JSON.stringify({
            roomId: urlRoomId,
            participantIdentity,
          })

          navigator.sendBeacon(
            `${supabaseUrl}/functions/v1/meeting-leave`,
            new Blob([payload], { type: 'application/json' })
          )
        }
      }
    }

    window.addEventListener('beforeunload', handleBeforeUnload)
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload)
    }
  }, [urlRoomId, participantIdentity, isConnected])

  const handleJoinMeeting = useCallback(async (deviceIds?: { audioDeviceId: string | null; videoDeviceId: string | null }) => {
    if (!urlRoomId || !displayName.trim()) return

    setIsJoining(true)

    try {
      // Use a persistent identity stored in localStorage for guests
      // This prevents duplicate participant records on page refresh
      const storageKey = `meeting_guest_identity_${urlRoomId}`
      let identity = localStorage.getItem(storageKey)
      if (!identity) {
        identity = generateParticipantIdentity()
        localStorage.setItem(storageKey, identity)
      }

      const token = await generateMeetingToken(urlRoomId, displayName, identity)
      const url = getLiveKitUrl()

      // Track participant identity for leave tracking
      setParticipantIdentity(identity)

      // Pass device IDs to LiveKit
      await connect(url, token, {
        audioDeviceId: deviceIds?.audioDeviceId,
        videoDeviceId: deviceIds?.videoDeviceId,
      })

      // Join meeting in database (as guest)
      await joinMeeting(urlRoomId, displayName, identity)
    } catch (err) {
      console.error('Failed to join meeting:', err)
    } finally {
      setIsJoining(false)
    }
  }, [urlRoomId, displayName, connect, joinMeeting])

  const handleLeaveMeeting = useCallback(async () => {
    // Track leave in database
    if (urlRoomId && participantIdentity) {
      await leaveMeeting(urlRoomId, participantIdentity)
    }

    disconnect()
    setParticipantIdentity(null)
    // Navigate back to a thank you / goodbye state or just show lobby
    setView('lobby')
    setDisplayName('')
  }, [disconnect, urlRoomId, participantIdentity, leaveMeeting])

  // No room ID provided
  if (!urlRoomId) {
    return (
      <div className="min-h-screen bg-[var(--bg-main)] flex items-center justify-center p-4">
        <div className="bg-[var(--bg-card)] border border-[var(--border)] rounded-xl p-8 max-w-md w-full text-center">
          <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <h1 className="text-xl font-semibold text-[var(--text-main)] mb-2">
            Invalid Meeting Link
          </h1>
          <p className="text-[var(--text-muted)]">
            This meeting link is invalid or has expired. Please check the link and try again.
          </p>
        </div>
      </div>
    )
  }

  // Show configuration error
  if (configError) {
    return (
      <div className="min-h-screen bg-[var(--bg-main)] flex items-center justify-center p-4">
        <div className="bg-[var(--bg-card)] border border-[var(--border)] rounded-xl p-8 max-w-md w-full text-center">
          <div className="w-16 h-16 mx-auto rounded-full bg-red-500/10 flex items-center justify-center mb-4">
            <Video size={32} className="text-red-400" />
          </div>
          <h2 className="text-xl font-bold text-[var(--text-main)] mb-2">Configuration Error</h2>
          <p className="text-[var(--text-muted)]">{configError}</p>
        </div>
      </div>
    )
  }

  // Meeting view - full screen
  if (view === 'meeting' && isConnected) {
    // Get remote participants for audio rendering
    const remoteParticipants = participants.filter(p => !p.isLocal)

    return (
      <div className="min-h-screen bg-[var(--bg-main)] flex flex-col">
        {/* Hidden audio elements for remote participants */}
        {remoteParticipants.map(participant => (
          <div key={participant.identity}>
            {participant.audioTrack && (
              <RemoteAudio audioTrack={participant.audioTrack} />
            )}
            {participant.screenAudioTrack && (
              <RemoteAudio audioTrack={participant.screenAudioTrack} />
            )}
          </div>
        ))}

        <MeetingRoom
          roomId={urlRoomId}
          participants={participants}
          localParticipant={localParticipant}
          isMuted={isMuted}
          isCameraOff={isCameraOff}
          isScreenSharing={isScreenSharing}
          isBackgroundBlurEnabled={isBackgroundBlurEnabled}
          isBackgroundBlurLoading={isBackgroundBlurLoading}
          blurStrength={blurStrength}
          showParticipants={showParticipants}
          onToggleMute={toggleMute}
          onToggleCamera={toggleCamera}
          onToggleScreenShare={toggleScreenShare}
          onToggleBackgroundBlur={toggleBackgroundBlur}
          onBlurStrengthChange={setBlurStrength}
          onToggleParticipants={() => setShowParticipants(!showParticipants)}
          onSwitchAudioInput={switchAudioInput}
          onSwitchVideoInput={switchVideoInput}
          onSwitchAudioOutput={switchAudioOutput}
          onApplyAudioSettings={applyAudioSettings}
          onLeave={handleLeaveMeeting}
        />
      </div>
    )
  }

  // Lobby view - centered with branding
  return (
    <div className="min-h-screen bg-[var(--bg-main)] flex flex-col">
      {/* Simple header */}
      <header className="p-4 border-b border-[var(--border)]">
        <div className="max-w-lg mx-auto flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-[var(--primary)]/10 flex items-center justify-center">
            <Video size={20} className="text-[var(--primary)]" />
          </div>
          <div>
            <h1 className="font-semibold text-[var(--text-main)]">Nexus Meeting</h1>
            <p className="text-sm text-[var(--text-muted)]">Join as a guest</p>
          </div>
        </div>
      </header>

      {/* Main content */}
      <main className="flex-1 flex items-center justify-center p-4">
        <div className="w-full max-w-lg">
          {roomError && (
            <div className="mb-4 p-3 bg-red-500/10 border border-red-500/20 rounded-lg text-red-400 text-sm">
              {roomError}
            </div>
          )}

          <MeetingLobby
            roomId={urlRoomId}
            displayName={displayName}
            onDisplayNameChange={setDisplayName}
            onJoin={handleJoinMeeting}
            isJoining={isJoining}
          />
        </div>
      </main>

      {/* Footer */}
      <footer className="p-4 border-t border-[var(--border)] text-center">
        <p className="text-xs text-[var(--text-muted)]">
          Powered by Nexus Dashboard
        </p>
      </footer>
    </div>
  )
}

// Wrapper component that provides necessary context
export function GuestMeetingPage() {
  return (
    <ThemeProvider>
      <LiveKitProvider>
        <GuestMeetingContent />
      </LiveKitProvider>
    </ThemeProvider>
  )
}

export default GuestMeetingPage
